import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import type { PdfTemplateSection } from '../../types/pdfTemplate';

interface PdfLabelOverridesProps {
  labels: Record<string, string>;
  sections: PdfTemplateSection[];
  onChange: (labels: Record<string, string>) => void;
}

const PdfLabelOverrides: React.FC<PdfLabelOverridesProps> = ({ labels, sections, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const entries = Object.entries(labels);

  const updateLabel = (key: string, value: string) => {
    onChange({ ...labels, [key]: value });
  };

  const removeLabel = (key: string) => {
    const updated = { ...labels };
    delete updated[key];
    onChange(updated);
  };

  const addLabel = () => {
    if (!newKey.trim()) return;
    onChange({ ...labels, [newKey.trim()]: newValue.trim() });
    setNewKey('');
    setNewValue('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Custom Label Overrides</h3>
        <p className="text-xs text-gray-500 mt-1">
          Override default labels used in the PDF. Use the key to reference a label, and the value
          will replace the default text.
        </p>
      </div>

      {/* Existing labels */}
      <div className="space-y-2">
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-center space-x-3">
            <div className="flex-1">
              <input
                type="text"
                value={key}
                disabled
                className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg font-mono text-gray-600"
              />
            </div>
            <span className="text-gray-400">&rarr;</span>
            <div className="flex-1">
              <input
                type="text"
                value={value}
                onChange={e => updateLabel(key, e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <button
              onClick={() => removeLabel(key)}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {entries.length === 0 && (
        <div className="text-center py-6 text-sm text-gray-400 bg-gray-50 rounded-lg">
          No custom labels defined. Add one below.
        </div>
      )}

      {/* Add new label */}
      <div className="border-t pt-4">
        <p className="text-xs text-gray-500 mb-2">Add new label override</p>
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            placeholder="Label key (e.g., documentTitle)"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 font-mono"
          />
          <span className="text-gray-400">&rarr;</span>
          <input
            type="text"
            value={newValue}
            onChange={e => setNewValue(e.target.value)}
            placeholder="Custom value"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            onKeyDown={e => e.key === 'Enter' && addLabel()}
          />
          <button
            onClick={addLabel}
            disabled={!newKey.trim()}
            className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:bg-brand-200 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            <span>Add</span>
          </button>
        </div>
      </div>

      {/* Common label keys reference */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-xs font-medium text-gray-600 mb-2">Common Label Keys</p>
        <div className="grid grid-cols-2 gap-1 text-xs text-gray-500">
          <span className="font-mono">documentTitle</span>
          <span className="font-mono">signatures</span>
          <span className="font-mono">notes</span>
          <span className="font-mono">terms</span>
          <span className="font-mono">shipTo</span>
          <span className="font-mono">total</span>
        </div>
      </div>
    </div>
  );
};

export default PdfLabelOverrides;
